import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { queryClient } from "~/repository";
import { RESCHEDULE_ALL_CALENDAR_EVENTS_SUCCESS } from "~/store/action_types";
import {
  editCalendarEvent,
  rescheduleAllEvents,
  rescheduleExceptionEvent,
  rescheduleOnlyThisEvent,
  rescheduleThisAndFutureEvents,
} from "~/store/actions/actions.calendarEvents";
import { CalendarEventRescheduleDialog } from "./CalendarEventRescheduleDialog";
import { EditOptionsTypes, isRepeating, prepareAttributes, resolveEditOptions } from "../Calendar/calendarUtils";
import { useObjectSheetContext } from "../Sheet/ObjectSheetContext";
import { showErrorToast } from "../Toast";

const Context = React.createContext({
  setEvent: () => null,
  rescheduleEvent: () => null,
});

export const useCalendarEventRescheduleContext = () => React.useContext(Context);

export const CalendarEventRescheduleContext = ({ source, children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeSheet } = useObjectSheetContext();
  const [event, setEvent] = useState(null);
  const [open, setOpen] = useState(false);
  const [attributes, setAttributes] = useState(null);
  const [editOptions, setEditOptions] = useState([]);
  const [disabled, setDisabled] = useState(false);

  const submitDialogCallback = useCallback(
    (action) => {
      if (action.error) {
        showErrorToast(t("Unable to save changes."));
        setDisabled(false);
        return action;
      } else {
        const { response = {} } = action;
        const { entities = {}, result = {} } = response;
        const { calendarEvents = {} } = entities;
        const { single = 0 } = result;
        const _event = calendarEvents[single];
        if (_event) {
          if (source === "sheet") {
            if (action.type !== RESCHEDULE_ALL_CALENDAR_EVENTS_SUCCESS) {
              navigate(`?modal=CalendarEvent-${_event.id}-${_event.calendar_id}`);
            } else {
              closeSheet();
            }
          } else if (source === "single") {
            if (action.type !== RESCHEDULE_ALL_CALENDAR_EVENTS_SUCCESS) {
              navigate(`/calendars/${_event.calendar_id}/events/${_event.id}`);
            } else {
              navigate("/calendar");
            }
          }
        }
        setOpen(false);
        setDisabled(false);
        return action;
      }
    },
    [closeSheet, navigate, source, t]
  );

  const actionCallback = useCallback((action) => {
    return action;
  }, []);

  const handleSubmit = useCallback(
    (action, values = null) => {
      const data = values ? prepareAttributes(action, event, values) : prepareAttributes(action, event, attributes);

      setDisabled(true);
      switch (action) {
        case EditOptionsTypes.ONLY_THIS: {
          return dispatch(rescheduleOnlyThisEvent(event.id, event.calendar_id, data)).then(submitDialogCallback);
        }
        case EditOptionsTypes.THIS_AND_FOLLOWING: {
          return dispatch(rescheduleThisAndFutureEvents(event.id, event.calendar_id, data)).then(submitDialogCallback);
        }
        case EditOptionsTypes.ALL: {
          return dispatch(rescheduleAllEvents(event.id, event.calendar_id, data)).then(submitDialogCallback);
        }
        default:
          break;
      }
    },
    [attributes, dispatch, event, submitDialogCallback]
  );

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    queryClient.invalidateQueries(["calendar-events"]);
  }, []);

  const rescheduleEvent = useCallback(
    (_attributes) => {
      const availableEditOptions = resolveEditOptions(event, _attributes);
      if (availableEditOptions.length === 0) {
        if (event?.recurrence_instance && !isRepeating(event)) {
          return dispatch(rescheduleExceptionEvent(event.id, event.calendar_id, _attributes)).then(actionCallback);
        }
        if (isRepeating(event)) {
          return dispatch(
            editCalendarEvent(event.id, event.calendar_id, prepareAttributes("edit_repeatig_event", event, _attributes))
          ).then(actionCallback);
        }
        return dispatch(editCalendarEvent(event.id, event.calendar_id, _attributes)).then(actionCallback);
      } else {
        if (availableEditOptions.length === 1) {
          return handleSubmit(availableEditOptions[0], _attributes);
        } else {
          setAttributes(_attributes);
          setEditOptions(availableEditOptions);
          setOpen(true);
          return new Promise((resolve) => resolve(true));
        }
      }
    },
    [actionCallback, dispatch, event, handleSubmit]
  );

  return (
    <Context.Provider
      value={{
        setEvent,
        rescheduleEvent,
      }}
    >
      {children}
      <>
        {event !== null ? (
          <CalendarEventRescheduleDialog
            options={editOptions}
            open={open}
            disabled={disabled}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onClose={handleClose}
            title={t("Edit Recurring Event")}
            labelText={t("Which event do you want to reschedule?")}
            submitBtnText={t("Update")}
          />
        ) : null}
      </>
    </Context.Provider>
  );
};

CalendarEventRescheduleContext.propTypes = {
  children: PropTypes.node.isRequired,
  source: PropTypes.oneOf(["calendar", "single", "sheet"]),
};
